import {gql} from '@apollo/client';
import React, {useState} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetChildEntitiesQuery,
  GetChildEntitiesQueryVariables,
} from 'registry/child-entities/__generated/GetChildEntitiesQuery';
import ChildEntityTable from 'registry/child-entities/ChildEntityTable';
import {MIN_QUERY_LENGTH, SHORT_QUERY_MESSAGE} from 'search/SearchConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {GenericEvent} from 'util/Types';

export const GET_CHILD_ENTITIES_QUERY = gql`
  query GetChildEntitiesQuery(
    $parentEntityId: StrictString!
    $childSchemaId: StrictString!
    $offset: StrictInt!
    $archivePurposes: [StrictString!]
    $queryString: StrictString!
  ) @skipAudit @engComponent(name: REGISTRY) {
    parentEntity: bioentity(id: $parentEntityId) {
      ... on RegistryItemInterface {
        id
        matchingChildEntities(
          childEntitySchemaId: $childSchemaId
          offset: $offset
          archivePurposes: $archivePurposes
          queryString: $queryString
        ) {
          total
          items {
            id
            archiveRecord {
              purpose
            }
            authors {
              ...AuthorGraphQL
            }
            createdAt
            name
            tags: tagsJson
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    childEntitySchema: tagSchema(id: $childSchemaId) {
      ...TagSchemaGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('Author').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface Props {
  childSchemaId: string;
  parentEntityId: string;
}

/**
 * Displays information on a schema's child entities for a specified child entity schema.
 */
export default function ChildEntityTab(props: Props): JSX.Element {
  const [archivePurposes, setArchivePurposes] = useState<Array<string>>([NOT_ARCHIVED_PURPOSE_STRING]);
  const [queryString, setQueryString] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);

  const searchInputTooltip =
    queryString.length > 0 && queryString.length < MIN_QUERY_LENGTH ? SHORT_QUERY_MESSAGE : '';

  return (
    <div {...getTestProps({component: 'ChildEntityTab', element: 'root'})}>
      <div className="u-lr-split u-small-spacing-bottom">
        <ArchivePurposeDropdown
          itemIds={[]}
          onChangePurposes={(purposes) => {
            setArchivePurposes(purposes);
            setOffset(0);
          }}
          selectedPurposes={archivePurposes}
        />
        <Tooltip title={searchInputTooltip} trigger="manual" open={searchInputTooltip !== ''}>
          <PlainSearchInput
            clearType={ClearType.X}
            placeholder="Search"
            onChange={(e: GenericEvent) => {
              setQueryString(e.target.value);
              setOffset(0);
            }}
            onEscape={() => {
              setQueryString('');
              setOffset(0);
            }}
            value={queryString}
          />
        </Tooltip>
      </div>
      <BenchlingQuery<GetChildEntitiesQuery, GetChildEntitiesQueryVariables>
        fetchPolicy="no-cache"
        query={GET_CHILD_ENTITIES_QUERY}
        renderLoading={() => <CenteredHorizontalThrobber />}
        variables={{
          parentEntityId: props.parentEntityId,
          childSchemaId: props.childSchemaId,
          archivePurposes,
          offset,
          queryString,
        }}
      >
        {(data) => {
          if (isInaccessible(data.parentEntity) || isInaccessible(data.childEntitySchema)) {
            assert(false, 'Parent entity or child entity schema should never be inaccessible');
            return null;
          }
          return (
            <ChildEntityTable
              childEntities={data.parentEntity.matchingChildEntities.items}
              childEntitySchema={graphQLToREST('TagSchema', data.childEntitySchema)}
              parentEntityId={props.parentEntityId}
              offset={offset}
              setOffset={setOffset}
              total={data.parentEntity.matchingChildEntities.total}
            />
          );
        }}
      </BenchlingQuery>
    </div>
  );
}
